import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { MessageCircle, Clock, User, Mail } from "lucide-react";
import { getCoachConversations } from "~/lib/functions/messaging";
import { formatDistanceToNow } from "date-fns";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/coach/_backend/messages/")({
  head: () => ({
    title: "Messages – Dream Coach Match",
    meta: [
      {
        name: "description",
        content: "View and manage your conversations with potential clients",
      },
    ],
  }),
  component: CoachMessagesPage,
});

function CoachMessagesPage() {
  const {
    data: conversationsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["coach-conversations"],
    queryFn: () => getCoachConversations({ data: { limit: 50, offset: 0 } }),
  });

  const conversations = conversationsData?.conversations || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Messages</h1>
          <p className="text-gray-600">Conversations with potential clients</p>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="h-12 w-12 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/4 rounded bg-gray-200" />
                    <div className="h-3 w-3/4 rounded bg-gray-200" />
                    <div className="h-3 w-1/2 rounded bg-gray-200" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Messages</h1>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <p className="text-red-600">
              Failed to load conversations. Please try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {conversations.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <MessageCircle className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h2 className="mb-2 text-xl font-semibold">No messages yet</h2>
            <p className="mb-6 text-gray-600">
              When users contact you through your profile, their messages will
              appear here. Make sure your contact mode is set to "messaging" in
              your client flow settings.
            </p>
            <Link to="/coach/edit-profile/client-flow">
              <div className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                Check Contact Settings
              </div>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {conversations.map((conversation) => {
            const userName =
              `${conversation.otherParticipant.firstName || ""} ${
                conversation.otherParticipant.lastName || ""
              }`.trim() || "User";

            return (
              <Link
                key={conversation.id}
                to="/coach/messages/$conversationId"
                params={{ conversationId: conversation.id }}
              >
                <Card className="transition-colors hover:bg-gray-50">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-blue-100 text-blue-600">
                          <User className="h-6 w-6" />
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-semibold text-gray-900">
                              {userName}
                            </h3>
                            <div className="flex items-center text-xs text-gray-500">
                              <Mail className="mr-1 h-3 w-3" />
                              New Contact
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {conversation.unreadCount > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {conversation.unreadCount} new
                              </Badge>
                            )}
                            <div className="flex items-center text-sm text-gray-500">
                              <Clock className="mr-1 h-3 w-3" />
                              {conversation.lastMessageAt
                                ? formatDistanceToNow(
                                    new Date(conversation.lastMessageAt),
                                    {
                                      addSuffix: true,
                                    },
                                  )
                                : "Unknown"}
                            </div>
                          </div>
                        </div>

                        <h4 className="mt-1 font-medium text-gray-800 truncate">
                          {conversation.subject}
                        </h4>

                        <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                          {conversation.senderType === "coach"
                            ? "You: "
                            : `${userName}: `}
                          {conversation.lastMessage.content}
                        </p>

                        {/* User Details Preview */}
                        <div className="mt-2 text-xs text-gray-500">
                          Potential client interested in your coaching services
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {conversations.length > 0 && (
        <div className="mt-8 rounded-lg bg-blue-50 p-4">
          <h3 className="font-medium text-blue-900">
            💡 Tips for client conversations
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-blue-800">
            <li>• Respond promptly to show you're professional and engaged</li>
            <li>• Ask clarifying questions about their goals and challenges</li>
            <li>• Share relevant examples from your coaching experience</li>
            <li>
              • Be clear about your coaching approach and what they can expect
            </li>
            <li>• Suggest a discovery call if there's mutual interest</li>
          </ul>
        </div>
      )}
    </div>
  );
}
